import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { RdsPaginatorPageEvent } from '@rds/core';
import { SinequaService } from '../../services/sinequa.service';
import { BreadcrumbsComponent } from '../../components/shared/breadcrumbs/breadcrumbs.component';
import { OfferingCardComponent } from '../../components/shared/offering-card/offering-card.component';
import {
  SinequaAggregation,
  SinequaAggregationItem,
  SinequaSearchRecord,
  SinequaSearchResponse,
} from '../../models/sinequa.model';
import { FormsModule } from '@angular/forms';
import {
  RdsButtonModule,
  RdsFormFieldModule,
  RdsPaginatorModule,
  RdsPaginatorComponent,
  RdsProgressSpinnerModule,
} from '@rds/angular-components';
import { SearchResultSkeletonComponent } from '../../components/panorama/search-result-skeleton/search-result-skeleton.component';
import { AssistantWidgetComponent } from '../../components/panorama/assistant-widget/assistant-widget.component';
import { FeatureFlagDirective } from '../../directives/feature-flag.directive';
import { SEARCH_RESULT_TEXTS } from '../../constants/panorama.constants';

// Filter types to display in the sidebar
const DISPLAY_FILTERS = [
  'Category',
  'OfferingType',
  'ApplicationType',
  'DataClassificationLevel',
  'ApprovalStatus',
  'gxp',
  'HostingType',
  'IsSupported',
  'LicenseCostResponsibility',
  'IsSingleSignOnSupported',
  'PerUserCostFrequency',
  'PerUserCostCurrency',
  'IsPerUserPricingAvailable',
];

/**
 * Sorts Sinequa actually honours, verified against the live index: `title`
 * reorders alphabetically, `relevance` is the default. `date` is accepted and
 * echoed back but returns relevance order, so the mock's "Recently added" is
 * not offered — it would be a control that silently does nothing. Unknown
 * values fall back to relevance without an error, so a bad value from the URL
 * is validated here rather than handed to the API to ignore.
 */
const SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'title', label: 'Name A–Z' },
] as const;

const DEFAULT_SORT = 'relevance';

interface ActiveFilter {
  aggregationName: string;
  column: string;
  value: string;
  display: string;
}

@Component({
  selector: 'inexp-search-result',
  imports: [
    BreadcrumbsComponent,
    OfferingCardComponent,
    FormsModule,
    RdsButtonModule,
    RdsFormFieldModule,
    RdsPaginatorModule,
    RdsProgressSpinnerModule,
    SearchResultSkeletonComponent,
    AssistantWidgetComponent,
    FeatureFlagDirective,
  ],
  templateUrl: './search-result.component.html',
  styleUrl: './search-result.component.scss',
  /**
   * Safe because every value the template reads is a signal or a computed —
   * including `sinequaService.loading()`/`.error()`. The helper methods the
   * template calls (`isFilterActive`, `getVisibleItems`,
   * `isAggregationExpanded`) read their signals *inside* the template's
   * reactive context, so those reads register as dependencies too; the rest are
   * pure over their arguments. Every async write lands in a signal, so nothing
   * here depends on a zone tick to be seen.
   */
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchResultComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  /** Public: the template reads `loading()` and `error()` off it directly. */
  readonly sinequaService = inject(SinequaService);

  readonly MAX_VISIBLE_ITEMS = 5;
  readonly PAGE_SIZE = 10;
  readonly SEARCH_RESULT_TEXTS = SEARCH_RESULT_TEXTS;

  // The catalog search field left this page with the mock's header, so the
  // search feature flag and the navigation service it fed go with it. New
  // queries now start from the homepage hero.

  /**
   * What the assistant panel talks about.
   *
   * Usually the typed query. Arriving from the mega menu's "See all in
   * <Category>" there is no query at all — the URL carries a category filter
   * instead — so the category stands in, and the panel has something to say
   * rather than sitting empty.
   */
  readonly assistantTopic = computed(() => {
    const query = this.searchQuery().trim();
    if (query) {
      return query;
    }
    return (
      this.activeFilters().find((f) => f.aggregationName === 'Category')
        ?.display ?? ''
    );
  });

  readonly SORT_OPTIONS = SORT_OPTIONS;
  readonly sortBy = signal<string>(DEFAULT_SORT);

  readonly breadcrumbs = [
    { text: 'Home', link: '/' },
    { text: 'Offerings', link: '/search' },
  ];

  /** `Search results for "x" (n results)`, or a plain count with no query. */
  readonly resultsHeading = computed(() => {
    const count = this.totalResults();
    const noun = count === 1 ? 'result' : 'results';
    const query = this.searchQuery().trim();
    return query
      ? `Search results for “${query}” (${count} ${noun})`
      : `Offerings (${count} ${noun})`;
  });

  /**
   * Phrase describing what a follow-up is about, for the chat's opening line.
   *
   * Without this a question asked from the results page arrived at the chat as
   * a bare sentence — "which of these are free?" with no "these" — so the
   * assistant answered about the whole catalog.
   */
  readonly assistantContext = computed(() => {
    const query = this.searchQuery().trim();
    if (query) {
      return `the search for "${query}"`;
    }
    const category = this.activeFilters().find(
      (f) => f.aggregationName === 'Category',
    )?.display;
    return category ? `the "${category}" category` : '';
  });

  readonly assistantSummaryPrompt = computed(() => {
    const topic = this.assistantTopic();
    if (!topic) {
      return '';
    }
    return (
      `A user is browsing the Panorama offering catalog for "${topic}". ` +
      'In two or three sentences, tell them what the catalog holds for that ' +
      'and how they might narrow it down. Do not list every offering.'
    );
  });

  searchQuery = signal<string>('');
  searchResults = signal<SinequaSearchResponse | null>(null);
  activeFilters = signal<ActiveFilter[]>([]);
  expandedAggregations = signal<Set<string>>(new Set());
  currentPage = signal(0);

  // 'full' = initial load / browser nav / new text query -> show skeleton.
  // 'refine' = filter toggle / pagination -> keep page, dim results only.
  searchMode = signal<'full' | 'refine'>('full');

  // Text query the option universe was last fetched for. Refetch the unfiltered
  // universe only when the query text changes, not on every filter refine.
  private universeQuery = signal<string | null>(null);

  // Monotonic tokens to drop stale responses when a newer search supersedes one.
  private searchSeq = 0;
  private facetSeq = 0;

  // Full skeleton only during a full-mode load; refines keep prior content.
  readonly showFullSkeleton = computed(
    () => this.sinequaService.loading() && this.searchMode() === 'full',
  );

  // Store initial aggregations from the first search (without filters)
  // These are used to display filter options and don't change when filters are applied
  private initialAggregations = signal<SinequaAggregation[]>([]);

  // Per-group OR counts: aggregationName -> (value -> count). For each group that
  // has an active selection we run an extra query that omits that group's own
  // filter (keeping other groups), so sibling options keep their real counts.
  private facetCounts = signal<Map<string, Map<string, number>>>(new Map());

  // Flag to prevent subscription from re-processing when we update the URL programmatically
  private isInternalNavigation = false;

  readonly paginator = viewChild(RdsPaginatorComponent);

  totalResults = computed(() => this.searchResults()?.totalRowCount || 0);

  // Use initial aggregations for display, but update counts from current results
  displayAggregations = computed(() => {
    const initial = this.initialAggregations();
    const current = this.searchResults()?.aggregations || [];

    // fallback to current aggregations if initial is empty
    const source = initial.length > 0 ? initial : current;

    // Only return empty if both initial and current aggregations are empty
    if (source.length === 0) return [];

    // Create a map of current counts for quick lookup
    const currentCountsMap = new Map<string, Map<string, number>>();
    current.forEach((agg: SinequaAggregation) => {
      const itemCounts = new Map<string, number>();
      agg.items?.forEach((item: SinequaAggregationItem) => {
        itemCounts.set(String(item.value), item.count);
      });
      currentCountsMap.set(agg.name, itemCounts);
    });

    const active = this.activeFilters();
    const facetCounts = this.facetCounts();

    return source
      .filter(
        (agg: SinequaAggregation) =>
          DISPLAY_FILTERS.includes(agg.name) &&
          agg.items &&
          agg.items.length > 0,
      )
      .map((agg: SinequaAggregation) => {
        const hasSelection = active.some(
          (f) => f.aggregationName.toLowerCase() === agg.name.toLowerCase(),
        );

        // Group with a selection: use the OR counts (own filter excluded, other
        // groups still applied) so siblings keep their real availability under
        // the remaining filters. A value absent from the OR result means it is
        // blocked by another active group -> count 0 (so it gets dropped). Only
        // fall back to the initial unfiltered count while the OR query has not
        // returned yet (no map entry for this group at all).
        // Other groups: use the AND-filtered main-query counts.
        // Either way, drop options that resolve to 0 unless they are currently
        // checked (so the active selection stays visible and removable).
        const orCounts = hasSelection ? facetCounts.get(agg.name) : undefined;

        return {
          ...agg,
          items: agg.items
            ?.map((item: SinequaAggregationItem) => {
              const count = hasSelection
                ? orCounts
                  ? orCounts.get(String(item.value)) ?? 0
                  : item.count ?? 0
                : currentCountsMap.get(agg.name)?.get(String(item.value)) ?? 0;
              // 0-result options stay visible but disabled; a currently-active
              // option is never disabled so it remains removable.
              const disabled =
                count === 0 && !this.isFilterActive(agg.name, item.value);
              return { ...item, count, disabled };
            })
            // Enabled options first, disabled (0-result) last; alphabetical
            // within each partition.
            .sort((a, b) => {
              if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
              return this.getItemDisplay(a).localeCompare(
                this.getItemDisplay(b),
              );
            }),
        };
      })
      // Drop only groups whose option universe is genuinely empty.
      .filter((agg: SinequaAggregation) => (agg.items?.length ?? 0) > 0)
      // Order sections by the fixed DISPLAY_FILTERS priority.
      .sort(
        (a: SinequaAggregation, b: SinequaAggregation) =>
          DISPLAY_FILTERS.indexOf(a.name) - DISPLAY_FILTERS.indexOf(b.name),
      );
  });

  getMatchHighlights(result: SinequaSearchRecord): string {
    const extracts = result.extracts;

    if (!extracts || !Array.isArray(extracts) || extracts.length === 0) {
      return '';
    }

    return extracts
      .slice(0, 2)
      .map((extract) => {
        if (extract && extract.highlighted) {
          return extract.highlighted
            .replace(/{b}/g, '<b>')
            .replace(/{nb}/g, '</b>');
        }
        return '';
      })
      .filter((text) => text)
      .join(' ... ');
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      // Skip processing if this navigation was triggered by our own code
      if (this.isInternalNavigation) {
        this.isInternalNavigation = false;
        return;
      }

      // Default to an empty query so `/search` (no `?q=`) behaves like `/search?q=`
      const query = params['q'] ?? '';
      this.searchQuery.set(query);

      // Sort is URL state too, so a reload or a shared link keeps it. An
      // unknown value would be silently ignored by Sinequa, so it is checked
      // against the offered options rather than passed straight through.
      const sort = params['sort'];
      this.sortBy.set(
        SORT_OPTIONS.some((o) => o.value === sort) ? sort : DEFAULT_SORT,
      );

      // Parse filters from URL
      const filtersFromUrl = this.parseFiltersFromUrl(params);
      this.activeFilters.set(filtersFromUrl);

      // Parse page from URL (1-based in URL, 0-based internally)
      const pageFromUrl = parseInt(params['page'], 10) || 1;
      this.currentPage.set(pageFromUrl - 1);

      // URL-driven navigation (initial load, deep link, back/forward) is a full
      // load -> show the skeleton, not the refine overlay.
      this.searchMode.set('full');

      // Fetch the unfiltered option universe for this query so the sidebar shows
      // all options with correct counts even when deep-linked with filters.
      this.ensureUniverse(query);

      this.performSearch(query, filtersFromUrl, pageFromUrl);
    });
  }

  /**
   * Load the unfiltered aggregation "universe" for a query (all option values
   * with their unfiltered counts). Runs once per distinct query text; filter
   * refines reuse it. Uses trackLoading=false so it never toggles the skeleton.
   */
  private ensureUniverse(query: string): void {
    if (this.universeQuery() === query && this.initialAggregations().length > 0) {
      return;
    }
    this.universeQuery.set(query);

    this.sinequaService
      .search(
        { query, page: 1, pageSize: 1, filters: [], columns: [] },
        false,
      )
      .subscribe((response) => {
        const aggregations: SinequaAggregation[] = response?.aggregations ?? [];
        if (aggregations.length > 0) {
          this.initialAggregations.set(aggregations);
        }
      });
  }

  /**
   * Parse filters from URL query parameters.
   * Each filter column is its own param: ?categories=value1,value2&offeringtype=value3
   * Comma-separated values within one param = multiple selections for that column.
   */
  private parseFiltersFromUrl(params: Record<string, string>): ActiveFilter[] {
    const filters: ActiveFilter[] = [];
    const reserved = new Set(['q', 'page', 'sort']);

    for (const [param, rawValue] of Object.entries(params)) {
      if (reserved.has(param) || !rawValue) continue;

      const aggregationName = this.getAggregationNameByColumn(param);
      const isKnown = DISPLAY_FILTERS.some(
        (f) => f.toLowerCase() === aggregationName.toLowerCase(),
      );
      if (!isKnown) continue;

      for (const value of rawValue.split(',').map((v) => v.trim()).filter(Boolean)) {
        filters.push({ aggregationName, column: param, value, display: value });
      }
    }

    return filters;
  }

  /**
   * Get aggregation name from column name
   */
  private getAggregationNameByColumn(column: string): string {
    const columnToAggregation: Record<string, string> = {
      category: 'Category',
      categories: 'Category',
      offeringtype: 'OfferingType',
      offeringtypes: 'OfferingType',
      applicationtype: 'ApplicationType',
      applicationtypes: 'ApplicationType',
      dataclassificationlevel: 'DataClassificationLevel',
      dataclassificationlevels: 'DataClassificationLevel',
      approvalstatus: 'ApprovalStatus',
      approvalstatuses: 'ApprovalStatus',
      gxp: 'gxp',
      hostingtype: 'HostingType',
      hostingtypes: 'HostingType',
      issupported: 'IsSupported',
      licensecostresponsibility: 'LicenseCostResponsibility',
      issinglesignonsupported: 'IsSingleSignOnSupported',
      perusercostfrequency: 'PerUserCostFrequency',
      perusercostcurrency: 'PerUserCostCurrency',
      isperuserpricingavailable: 'IsPerUserPricingAvailable',
    };
    return columnToAggregation[column.toLowerCase()] || column;
  }

  /**
   * Build per-column query params from the active filter list.
   * Multiple values for the same column are comma-separated.
   * Returns null for columns that are no longer active (so Angular removes them).
   */
  private buildFilterQueryParams(
    filters: ActiveFilter[],
  ): Record<string, string | null> {
    const byColumn = new Map<string, string[]>();
    for (const f of filters) {
      if (!byColumn.has(f.column)) byColumn.set(f.column, []);
      byColumn.get(f.column)!.push(f.value);
    }

    const result: Record<string, string | null> = {};
    for (const [col, values] of byColumn) {
      result[col] = values.join(',');
    }
    return result;
  }

  /**
   * Update URL without triggering the subscription to re-process.
   * Replaces all query params explicitly so stale filter columns are removed.
   */
  private updateUrlSilent(
    query: string,
    filters: ActiveFilter[],
    page = 1,
  ): void {
    const queryParams: Record<string, string | null> = {
      q: query,
      page: page > 1 ? String(page) : null,
      // Omitted at the default so a plain search keeps a clean URL.
      sort: this.sortBy() === DEFAULT_SORT ? null : this.sortBy(),
      ...this.buildFilterQueryParams(filters),
    };

    this.isInternalNavigation = true;

    this.router.navigate(['/search'], {
      queryParams,
      replaceUrl: true,
    });
  }

  performSearch(query: string, filters?: ActiveFilter[], page = 1): void {
    const seq = ++this.searchSeq;

    const searchFilters = filters?.map((f) => ({
      field: f.column,
      value: f.value,
    }));

    this.sinequaService
      .search({
        query: query,
        page: page,
        pageSize: this.PAGE_SIZE,
        sort: this.sortBy(),
        filters: searchFilters,
        columns: [
          'title',
          'summary',
          'description',
          'url',
          'url1',
          'modified',
          'author',
          'documentType',
          'fileSize',
          'slug',
          'approvalStatus',
          'categories',
          'language',
          // `matchingpassages` fed the neural-search panel and nothing else;
          // the card highlights come from `extracts`.
          'extracts',
        ],
      })
      .subscribe((response) => {
        // Drop stale responses: a newer search has already superseded this one.
        if (seq !== this.searchSeq) return;

        this.searchResults.set(response);

        const aggregations: SinequaAggregation[] = response.aggregations || [];

        // Seed the option universe from this response only when it is itself
        // unfiltered (otherwise the universe would be incomplete). Deep-linked
        // filtered loads get their universe from ensureUniverse() instead.
        if (!filters || filters.length === 0) {
          this.initialAggregations.set(aggregations);
        }

        // Reconcile activeFilters against the real Sinequa aggregations so
        // isFilterActive always works regardless of casing/naming differences
        // in our mapping table, or when the URL carries the human-readable
        // display name (e.g. from the mega-menu/footer) instead of item.value.
        if (aggregations.length > 0) {
          this.activeFilters.update((current) =>
            current.map((f) => {
              const match = aggregations.find(
                (agg) =>
                  agg.column?.toLowerCase() === f.column.toLowerCase() ||
                  agg.name?.toLowerCase() === f.aggregationName.toLowerCase(),
              );
              if (!match) return f;

              // Match the URL value against either item.value or item.display
              // and normalize to the canonical item.value the checkboxes bind to.
              const item = match.items?.find(
                (it) =>
                  String(it.value).toLowerCase() === f.value.toLowerCase() ||
                  String(it.display ?? '').toLowerCase() ===
                    f.value.toLowerCase(),
              );

              return {
                ...f,
                aggregationName: match.name,
                value: item ? String(item.value) : f.value,
                display: item ? String(item.display ?? item.value) : f.display,
              };
            }),
          );
        }

        // Reset expanded ("show more") state only on full loads; keep it on a
        // filter refine so the user's expanded sections don't collapse.
        if (this.searchMode() === 'full') {
          this.expandedAggregations.set(new Set());
        }

        // Recompute per-group OR counts using the reconciled filter names.
        this.loadFacetCounts(query, this.activeFilters());
      });
  }

  /**
   * For each active-filter group, run a silent query that omits that group's own
   * filters (keeping all other groups) and capture its aggregation counts. This
   * gives OR-within-group / AND-across-group counts so a selected group's sibling
   * options keep their real availability. No active filters -> clear the map.
   */
  private loadFacetCounts(query: string, filters: ActiveFilter[]): void {
    const seq = ++this.facetSeq;
    const columns = [...new Set(filters.map((f) => f.column))];
    if (columns.length === 0) {
      this.facetCounts.set(new Map());
      return;
    }

    const requests = columns.map((col) => {
      const others = filters
        .filter((f) => f.column !== col)
        .map((f) => ({ field: f.column, value: f.value }));
      return this.sinequaService.search(
        { query, page: 1, pageSize: 1, filters: others, columns: [] },
        false,
      );
    });

    forkJoin(requests).subscribe((responses) => {
      // Drop stale fan-out results superseded by a newer filter change.
      if (seq !== this.facetSeq) return;

      const next = new Map<string, Map<string, number>>();
      responses.forEach((response, i) => {
        const col = columns[i];
        const aggregations: SinequaAggregation[] = response?.aggregations ?? [];
        const agg = aggregations.find(
          (a) =>
            a.column?.toLowerCase() === col.toLowerCase() ||
            a.name?.toLowerCase() ===
              this.getAggregationNameByColumn(col).toLowerCase(),
        );
        if (!agg) return;
        const counts = new Map<string, number>();
        agg.items?.forEach((item) =>
          counts.set(String(item.value), item.count),
        );
        next.set(agg.name, counts);
      });
      this.facetCounts.set(next);
    });
  }

  handlePageEvent(event: RdsPaginatorPageEvent): void {
    const page = event.pageIndex + 1; // Sinequa uses 1-based page indexing

    // Paging refines the current result set -> keep the page, no skeleton.
    this.searchMode.set('refine');

    // Update state immediately
    this.currentPage.set(event.pageIndex);

    // Update URL silently and perform search
    this.updateUrlSilent(this.searchQuery(), this.activeFilters(), page);
    this.performSearch(this.searchQuery(), this.activeFilters(), page);

    // Scroll to top of results
    this.scrollToTop();
  }

  /**
   * Re-run the current query under a different sort.
   *
   * Goes through the URL rather than calling performSearch directly, so the
   * choice survives a reload or a shared link like every other search state.
   */
  onSortChange(sort: string): void {
    this.sortBy.set(sort);
    this.currentPage.set(0);
    this.paginator()?.firstPage();
    this.updateUrlSilent(this.searchQuery(), this.activeFilters(), 1);
    this.performSearch(this.searchQuery(), this.activeFilters(), 1);
  }

  /**
   * Check if aggregation has more items than the visible limit
   */
  hasMoreItems(aggregation: SinequaAggregation): boolean {
    return (aggregation.items?.length || 0) > this.MAX_VISIBLE_ITEMS;
  }

  /**
   * Get the count of hidden items
   */
  getHiddenItemsCount(aggregation: SinequaAggregation): number {
    return Math.max(
      0,
      (aggregation.items?.length || 0) - this.MAX_VISIBLE_ITEMS,
    );
  }

  /**
   * Get visible items for an aggregation (first 5 or all if expanded)
   */
  getVisibleItems(aggregation: SinequaAggregation): SinequaAggregationItem[] {
    const items = aggregation.items || [];
    if (
      this.isAggregationExpanded(aggregation.name) ||
      items.length <= this.MAX_VISIBLE_ITEMS
    ) {
      return items;
    }
    return items.slice(0, this.MAX_VISIBLE_ITEMS);
  }

  // Filter methods
  toggleAggregation(name: string): void {
    const expanded = new Set(this.expandedAggregations());
    if (expanded.has(name)) {
      expanded.delete(name);
    } else {
      expanded.add(name);
    }
    this.expandedAggregations.set(expanded);
  }

  isAggregationExpanded(name: string): boolean {
    return this.expandedAggregations().has(name);
  }

  isFilterActive(
    aggregationName: string,
    value: string | boolean | number,
  ): boolean {
    const normAgg = aggregationName.toLowerCase();
    const normVal = String(value).toLowerCase();
    return this.activeFilters().some(
      (f) =>
        f.aggregationName.toLowerCase() === normAgg &&
        f.value.toLowerCase() === normVal,
    );
  }

  toggleFilter(
    aggregation: SinequaAggregation,
    item: SinequaAggregationItem,
  ): void {
    const currentFilters = [...this.activeFilters()];
    const existingIndex = currentFilters.findIndex(
      (f) =>
        f.aggregationName === aggregation.name &&
        f.value === String(item.value),
    );

    if (existingIndex >= 0) {
      // Remove filter
      currentFilters.splice(existingIndex, 1);
    } else {
      // Add filter
      currentFilters.push({
        aggregationName: aggregation.name,
        column: aggregation.column,
        value: String(item.value),
        display: String(item.display ?? item.value),
      });
    }

    // Toggling a filter refines the current results -> no skeleton.
    this.searchMode.set('refine');

    // Reset pagination when filters change
    this.currentPage.set(0);
    this.paginator()?.firstPage();

    // Update state immediately to prevent race conditions
    this.activeFilters.set(currentFilters);

    // Update URL (skipLocationChange to prevent subscription from re-triggering)
    this.updateUrlSilent(this.searchQuery(), currentFilters);

    // Perform search directly
    this.performSearch(this.searchQuery(), currentFilters);
  }

  removeFilter(filter: ActiveFilter): void {
    const currentFilters = this.activeFilters().filter(
      (f) =>
        !(
          f.aggregationName === filter.aggregationName &&
          f.value === filter.value
        ),
    );

    // Removing a filter refines the current results -> no skeleton.
    this.searchMode.set('refine');

    // Reset pagination when filters change
    this.currentPage.set(0);
    this.paginator()?.firstPage();

    // Update state immediately
    this.activeFilters.set(currentFilters);

    // Update URL silently and perform search
    this.updateUrlSilent(this.searchQuery(), currentFilters);
    this.performSearch(this.searchQuery(), currentFilters);

    // Scroll to top of results
    this.scrollToTop();
  }

  clearAllFilters(): void {
    // Clearing filters refines the current results -> no skeleton.
    this.searchMode.set('refine');

    // Reset pagination when filters are cleared
    this.currentPage.set(0);
    this.paginator()?.firstPage();

    // Update state immediately
    this.activeFilters.set([]);

    // Update URL silently and perform search
    this.updateUrlSilent(this.searchQuery(), []);
    this.performSearch(this.searchQuery(), []);

    // Scroll to top of results
    this.scrollToTop();
  }

  private scrollToTop(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getItemDisplay(item: SinequaAggregationItem): string {
    // Handle multilingual display format like "< 10 Kb[fr]< 10 Ko"
    // Also handle non-string values (e.g., booleans)
    const rawDisplay = item.display ?? item.value;
    const display = String(rawDisplay);
    if (display.includes('[')) {
      return display.split('[')[0].trim();
    }
    return display;
  }

  getAggregationDisplayName(name: string): string {
    const nameMap: Record<string, string> = {
      Category: 'Category',
      OfferingType: 'Offering Type',
      ApplicationType: 'Application Type',
      DataClassificationLevel: 'Data Classification Level',
      ApprovalStatus: 'Approval Status',
      gxp: 'GxP',
      HostingType: 'Hosting Type',
      IsSupported: 'Support Availability',
      LicenseCostResponsibility: 'License Cost Responsibility',
      IsSingleSignOnSupported: 'Is Single Sign-on Supported',
      PerUserCostFrequency: 'Per-User Cost Frequency',
      PerUserCostCurrency: 'Per-User Cost Currency',
      IsPerUserPricingAvailable: 'Per-User Pricing Available',
    };
    return nameMap[name] || name;
  }
}
